/**
 * 「今日の業務」のホーム（TODAY-HOME-ASTRA・根拠のモック `mockups/astra-store-design.html`）。
 *
 * 閉店後に開いた瞬間、**何が残っているか**と**今日の数字**を読めるようにする。
 * ★数えるだけ。新しい API は無い——タブが既に取っている応答（伝票・売上・仕入・小口・日報）を並べ直す。
 * ★売上は**税込**（締めレシートの数字そのもの）。税率の設定には寄りかからない。
 */
import type { DailySales, DocumentType, IntakeDocument, PettyCashEntry, Purchase } from "./api.js";

const WEEKDAY = ["日", "月", "火", "水", "木", "金", "土"];
const DOC_LABEL: Record<DocumentType, string> = {
  DELIVERY_NOTE: "納品書",
  RECEIPT: "買い出し領収書",
  POS_CLOSE: "締めレシート",
};

const yen = (n: number): string => `${n.toLocaleString("ja-JP")} 円`;

/** `2026-09-20` → `9月20日（日）の営業` */
export function businessDayHeading(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return `${String(d.getUTCMonth() + 1)}月${String(d.getUTCDate())}日（${WEEKDAY[d.getUTCDay()] ?? ""}）の営業`;
}

/** 仕入の合計 */
export function purchaseTotals(purchases: readonly Purchase[]): number {
  return purchases.reduce((sum, p) => sum + Number(p.amount), 0);
}

export interface TodayHomeInput {
  businessDate: string;
  documents: readonly IntakeDocument[];
  sales: DailySales | null;
  purchases: readonly Purchase[];
  pettyEntries: readonly PettyCashEntry[];
  /** 振り返りの行を出すか（manager 以上）。null＝出さない、それ以外＝保存済みか */
  reportSaved: boolean | null;
  open: { intake(): void; report(): void; records(): void };
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, text?: string, className?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (className !== undefined) node.className = className;
  return node;
};

function row(title: string, status: string, done: boolean, action: string, onClick: () => void): HTMLElement {
  const r = el("div", undefined, "today-row");
  const left = el("div");
  left.append(el("h3", title), el("span", status, done ? "today-status done" : "today-status"));
  const b = el("button", `${action} →`, "link");
  b.type = "button";
  b.addEventListener("click", onClick);
  r.append(left, b);
  return r;
}

export function todayHomeView(input: TodayHomeInput): HTMLElement {
  const root = el("section", undefined, "today-home");
  const head = el("div", undefined, "today-head");
  head.append(el("p", businessDayHeading(input.businessDate), "eyebrow"), el("h2", "今日の業務"));
  root.append(head);

  const grid = el("div", undefined, "today-grid");
  const main = el("div");

  const shoot = el("button", "伝票を撮る", "primary today-shoot");
  shoot.type = "button";
  shoot.addEventListener("click", input.open.intake);
  main.append(shoot, el("p", "納品書・買い出し領収書・Airレジ締めレシート", "muted"));

  const waiting = input.documents.filter((d) => d.workflow_status !== "REGISTERED").length;
  const salesDone = input.sales !== null;
  const allDone = waiting === 0 && salesDone && input.reportSaved !== false;

  const tasks = el("div", undefined, "today-tasks");
  tasks.append(el("h3", allDone ? "今日の登録状況" : "あと、確認すること", "today-tasks-title"));
  tasks.append(
    row("確認待ちの伝票", waiting === 0 ? "✓ ありません" : `● ${String(waiting)}件`, waiting === 0,
      waiting === 0 ? "見る" : "確認する", input.open.intake),
    row("締めレシート", salesDone ? "✓ 売上へ登録済み" : "● 売上は未登録", salesDone,
      salesDone ? "記録を見る" : "撮る", salesDone ? input.open.records : input.open.intake),
  );
  if (input.reportSaved !== null) {
    tasks.append(row("今日の振り返り", input.reportSaved ? "✓ 保存済み" : "● 未入力", input.reportSaved,
      input.reportSaved ? "報告へ" : "入力する", input.open.report));
  }
  const registered = (Object.keys(DOC_LABEL) as DocumentType[])
    .map((t) => [DOC_LABEL[t], input.documents.filter((d) => d.doc_type === t && d.workflow_status === "REGISTERED").length] as const)
    .filter(([, n]) => n > 0)
    .map(([label, n]) => `${label} ${String(n)}件`);
  if (registered.length > 0) tasks.append(el("p", `✓ ${registered.join("・")} 登録済み`, "muted"));
  main.append(tasks);

  const numbers = el("aside", undefined, "today-numbers");
  numbers.append(el("h3", "今日の数字"), el("div", "売上（税込）", "muted"));
  const amount = el("div", undefined, "today-amount");
  if (input.sales === null) amount.textContent = "—";
  else {
    amount.textContent = Number(input.sales.salesInclTaxDinner).toLocaleString("ja-JP");
    amount.append(el("span", "円", "unit"));
  }
  numbers.append(amount, el("p", salesDone ? "締めレシートから登録済み" : "締めレシートの確認後に反映", "muted"));

  const purchaseTotal = purchaseTotals(input.purchases);
  const spent = input.pettyEntries.filter((e) => e.direction === "OUT").reduce((a, e) => a + Number(e.amount), 0);
  const facts = el("dl", undefined, "today-facts");
  facts.append(el("dt", "仕入"), el("dd", yen(purchaseTotal)));
  facts.append(
    el("dt", "小口の支出"), el("dd", yen(spent)),
    el("dt", "客数"), el("dd", input.sales === null ? "未登録" : `${String(input.sales.guestsDinner)} 名`),
  );
  const records = el("button", "今日の記録を見る →", "link");
  records.type = "button";
  records.addEventListener("click", input.open.records);
  numbers.append(facts, records);

  grid.append(main, numbers);
  root.append(grid);
  return root;
}
